<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div class="app-container">
    <Header
      :enableDarkMode="enableDarkMode && !isUserAuthenticated"
      :logoRotation="isUserAuthenticated ? 0 : logoRotation"
      :showPricingLink="!isUserAuthenticated"
      @handleLoginSelection="handleLoginSelection"
      @handleSignUpSelection="handleSignUpSelection"
      @scrollToPricing="scrollToPricing"
    />

    <HeroSection
      v-show="!isUserAuthenticated"
      @bookDemo="handleBookDemo"
      @startTrial="handleStartTrial"
    />

    <ValueProposition
      v-show="!isUserAuthenticated"
      :items="valuePropositionItems"
    />

    <FeaturesSection v-show="!isUserAuthenticated" :features="featuresList" />

    <section v-show="!isUserAuthenticated" class="vision-section">
      <h1 class="main-title">Why choose Skyhost</h1>
      <p class="vision-description">
        Real benefits that help you deliver better results and grow your
        business.
      </p>
      <div class="benefits-container">
        <BenefitList :items="benefitsList" />
      </div>
    </section>

    <section v-show="!isUserAuthenticated" class="faq-home-section">
      <FAQSection
        mainHeading="Frequently asked questions"
        subHeading="Find answers to common questions."
        :categories="faqCategories"
      />
    </section>

    <SocialProof
      v-show="!isUserAuthenticated"
      :testimonials="testimonialsList"
    />

    <PricingPreview v-show="!isUserAuthenticated" />

    <FinalCTA v-show="!isUserAuthenticated" @startTrial="handleStartTrial" />

    <SiteFooter v-show="!isUserAuthenticated" :features="featuresList" />

    <SignUpModal
      :isOpen="isSignUpModalOpen"
      @close="closeSignUpModal"
      :onSubmit="handleSignUp"
      @openLoginModal="openLoginModal"
      :errorMessage="errorMessage"
    />

    <LoginModal
      :isOpen="isLoginModalOpen"
      @close="closeLoginModal"
      @openSignUpModal="openSignUpModal"
      :onSubmit="handleLoginButton"
      :errorMessage="errorMessage"
    />
  </div>
</template>

<script>
import { auth } from "@/plugins/firebase";
import { signOut } from "firebase/auth";
import { useSessionStore } from "@/stores/session";
import { amplitude } from "../plugins/amplitude";
import { loginService, userAuth } from "../services/userAuth";
import { truncateText } from "@/utils/helper";
import SignUpModal from "@/components/organisms/modal/signUpModal.vue";
import Header from "@/components/organisms/Header.vue";
import LoginModal from "../components/organisms/modal/loginModal.vue";
import HeroSection from "@/components/organisms/HeroSection.vue";
import ValueProposition from "@/components/organisms/ValueProposition.vue";
import FeaturesSection from "@/components/organisms/FeaturesSection.vue";
import FAQSection from "@/components/organisms/Faq.vue";
import SocialProof from "@/components/organisms/SocialProof.vue";
import PricingPreview from "@/components/organisms/PricingPreview.vue";
import FinalCTA from "@/components/organisms/FinalCTA.vue";
import SiteFooter from "@/components/organisms/SiteFooter.vue";
import BenefitList from "@/components/molecules/BenefitList.vue";
import dotenv from "dotenv";
import { isMobile } from "@/utils/helper";

// Load environment variables from .env file
dotenv.config();

export default {
  data() {
    return {
      errorMessage: "",
      isLoginModalOpen: false,
      isSignUpModalOpen: false,
      enableDarkMode: true,
      logoRotation: 0,
      lastScrollY: 0,
      isMobile: false,
      valuePropositionItems: [
        {
          icon: "message-circle",
          title: "Clear communication from start to finish",
          description:
            "Remove guesswork. Share live views, discuss issues instantly, and keep everyone aligned.",
        },
        {
          icon: "layers",
          title: "Workflows that cut out the back-and-forth",
          description:
            "Clients see exactly what you see. Teams stay coordinated. Projects move faster.",
        },
        {
          icon: "video",
          title: "Professional remote sessions",
          description:
            "Run inspections and monitoring jobs like premium live meetings—not passive video feeds.",
        },
        {
          icon: "dollar-sign",
          title: "Unlock new revenue streams",
          description:
            "Sell remote access, virtual walk-throughs, and premium client sessions without extra hardware.",
        },
      ],
      featuresList: [
        {
          imageSrc: require("@/assets/main-banner.png"),
          title: "Real-Time Live Feed",
          description:
            "Stream crisp, low-latency video directly from any DJI drone into a clean browser interface.",
        },
        {
          imageSrc: require("@/assets/feature_group_audio.png"),
          title: "Group Audio Collaboration",
          description:
            "Talk with clients and teams during the flight. Get instant instructions, answers, and confirmations.",
        },
        {
          imageSrc: require("@/assets/feature_clean_feed.png"),
          title: "Clean Feed (No OSD)",
          description:
            "Send a clean, distraction-free video feed to your clients with no on-screen telemetry or overlays in view.",
        },
        {
          imageSrc: require("@/assets/feature_dji.webp"),
          title: "Works with Any DJI Drone",
          description:
            "No special hardware. No enterprise upgrades. Just connect your DJI controller or app and fly.",
        },
        {
          imageSrc: require("@/assets/fearture_link.png"),
          title: "Client Access Links",
          description:
            "Share controlled, private links for each job. No apps, no setups, no tech support needed.",
        },
        {
          imageSrc: require("@/assets/feature_dashboard.png"),
          title: "Flight Logs",
          description:
            "Have a record of your previous flight sessions for future references and analytics.",
        },
        {
          imageSrc: require("@/assets/feature_browser.webp"),
          title: "Browser Based Streaming",
          description:
            "Join or host a flight directly in the browser (web or mobile browser) — no app installations needed",
        },
        {
          imageSrc: require("@/assets/feature_control.png"),
          title: "Privacy & Access Control",
          description:
            "Share a private session link with clients and teams. No apps, no accounts, no setup needed.",
        },
      ],
      benefitsList: [
        {
          title: "New Revenue",
          description:
            "Sell remote access as a premium service by inviting stakeholders, live viewing sessions, and delivering full oversight without travel.",
        },
        {
          title: "Time Savings",
          description:
            "Keep every stakeholder in the loop while you fly, replacing follow-up calls with a live workspace so projects move forward immediately.",
        },
        {
          title: "Better Communication",
          description:
            "Give clients shared video, audio, and map context so they can request angles or notes in real time—no mixed signals, no repeat flights.",
        },
        {
          title: "Faster Approvals",
          description:
            "Decision makers review progress live, add comments on the spot, and get more approvals for new projects.",
        },
        {
          title: "Higher Accuracy",
          description:
            "Clients guide captures live—requesting angles, zoom levels, or annotations—so nothing gets missed and revisits drop.",
        },
        {
          title: "Professional Experience",
          description:
            "A private session link and consistent workflow make every touchpoint feel intentional, signaling you run a modern, reliable operation.",
        },
      ],
      faqCategories: [
        {
          title: "Top questions",
          items: [
            {
              question: "What is Skyhost in simple terms?",
              answer:
                "Skyhost is a live-operations communication layer for professional drone work. It lets clients and teams join your flights in real time with ultra low-latency video and two-way audio.",
            },
            {
              question: "Does Skyhost work with my existing DJI drone?",
              answer:
                "Yes. Skyhost works with most the latest DJI drone that supports RTMP output. No extra hardware or enterprise upgrades are required. See our blog for more details.",
            },
            {
              question: "Do my clients need to install anything?",
              answer:
                "No. Clients join through a secure browser link—no apps, accounts, or setup needed.",
            },
            {
              question: "Is the latency actually low enough for live feedback?",
              answer:
                "Yes. Skyhost is built for real-time inspection workflows with ultra low-latency video and two-way audio. Clients can talk, guide, and request angles with near-instant audio.",
            },
          ],
        },
        {
          title: "Getting started",
          items: [
            {
              question: "How do I set up my first Skyhost session?",
              answer:
                "Create an account, generate a meeting link, and connect your DJI controller to the Skyhost RTMP endpoint. Once connected, start your flight and let clients join the session.",
            },
            {
              question: "Do I need any special equipment?",
              answer:
                "You only need your DJI drone, your controller, and a stable internet uplink such as 5G or a portable hotspot or Starlink for remote locations.",
            },
            {
              question: "How long does a typical setup take?",
              answer:
                "It takes about 2 minutes to complete the setup. Skyhost is designed to slip directly into your existing workflow.",
            },
          ],
        },
        {
          title: "Usage & workflow",
          items: [
            {
              question: "Can multiple clients join the same flight?",
              answer:
                "Yes. You can invite multiple engineers, supervisors, or stakeholders to join a single session simultaneously. Guest count is limited to the plan limit.",
            },
            {
              question: "Can my internal team join too?",
              answer:
                "Absolutely. Team members can join for training, oversight, or collaborative inspection work.",
            },
            {
              question: "Does Skyhost record the sessions?",
              answer:
                "No, Skyhost does not record the sessions or provide any recording features.",
            },
            {
              question: "What happens if my connection drops?",
              answer:
                "Skyhost will automatically attempt to reconnect the feed. Clients remain on the call and see the feed resume when connectivity returns.",
            },
          ],
        },
        {
          title: "Billing & subscription",
          items: [
            {
              question: "How is Skyhost priced?",
              answer:
                "Skyhost uses a simple subscription model with fair usage limits. You pay only for what your needs.",
            },
            {
              question: "Do I pay per flight or per client?",
              answer:
                "No. Skyhost pricing is not tied to per-flight or per-client fees. Teams can scale usage based on their workflow.",
            },
            {
              question: "Can I upgrade or downgrade anytime?",
              answer:
                "Yes. You can change your plan at any time, and adjustments are prorated automatically.",
            },
          ],
        },
        {
          title: "Technical requirements",
          items: [
            {
              question: "What internet speed do I need?",
              answer:
                "We recommend at least 20 Mbps upload and 80 Mbps download for consistent Full HD streaming. Upload speed matters more for this to work. Higher bandwidth provides better stability.",
            },
            {
              question: "Does Skyhost work internationally?",
              answer:
                "Yes. Skyhost is cloud-based and optimized for global access, allowing clients to join from anywhere.",
            },
            {
              question: "Is my data secure?",
              answer:
                "All sessions are encrypted. You control access, links, and permissions for every project.",
            },
          ],
        },
        {
          title: "Support & resources",
          items: [
            {
              question: "How do I get help if something goes wrong?",
              answer:
                "You can reach our support team via our email at hello@skyhost.ai",
            },
            {
              question: "Do you provide onboarding for teams?",
              answer:
                "Yes. Skyhost offers onboarding sessions for teams who want guidance integrating our workflow into daily operations.",
            },
            {
              question: "Where can I learn best practices?",
              answer:
                "We provide documentation, workflow guides, and industry-specific tips to help you run smooth, professional remote sessions.",
            },
          ],
        },
      ],
      testimonialsList: [
        {
          name: "Sarah Chen",
          title: "Construction Project Manager",
          quote:
            "Clients finally understand what's happening without long explanation calls. The live feed makes everything crystal clear.",
        },
        {
          name: "Marcus Rodriguez",
          title: "Infrastructure Inspector",
          quote:
            "Our team coordination improved instantly once we moved sessions into Skyhost. No more back-and-forth emails trying to explain what we're seeing.",
        },
        {
          name: "Jennifer Park",
          title: "Drone Operations Lead",
          quote:
            "Remote inspections became a premium service we could actually charge for. Clients love being able to join live and ask questions in real-time.",
        },
        {
          name: "David Thompson",
          title: "Site Supervisor",
          quote:
            "We cut our inspection time in half. When clients can see exactly what we're looking at, decisions happen faster and projects move forward.",
        },
        {
          name: "Amanda Foster",
          title: "Environmental Consultant",
          quote:
            "The two-way audio is a game changer. I can guide the pilot to specific areas while clients watch and understand the context immediately.",
        },
        {
          name: "James Wilson",
          title: "Real Estate Developer",
          quote:
            "We use Skyhost for all our site walkthroughs now. Investors can join remotely and see the property in real-time. It's professional and efficient.",
        },
        {
          name: "Lisa Martinez",
          title: "Safety Coordinator",
          quote:
            "Being able to share live sessions with multiple stakeholders means everyone stays on the same page. No more conflicting reports or confusion.",
        },
        {
          name: "Robert Kim",
          title: "Facilities Manager",
          quote:
            "The live feed is a game changer. I can see exactly what's happening on the site and make decisions in real-time.",
        },
        {
          name: "Emily Johnson",
          title: "Agricultural Surveyor",
          quote:
            "Our clients appreciate the transparency. They can see crop conditions live and we can discuss findings together. It builds trust.",
        },
        {
          name: "Michael Brown",
          title: "Pilot & Operator",
          quote:
            "The interface is clean and the connection is solid. I can focus on flying while clients get the view they need. Best tool in my kit.",
        },
        {
          name: "Rachel Green",
          title: "Event Operations Director",
          quote:
            "We use Skyhost for event planning and monitoring. Multiple team members can watch the same feed and coordinate in real-time.",
        },
        {
          name: "Chris Anderson",
          title: "Security Operations Manager",
          quote:
            "Skyhost has made our security operations more efficient and effective. We can now monitor multiple sites simultaneously and respond to incidents in real-time.",
        },
      ],
    };
  },
  components: {
    Header,
    SignUpModal,
    LoginModal,
    HeroSection,
    ValueProposition,
    FeaturesSection,
    FAQSection,
    SocialProof,
    PricingPreview,
    FinalCTA,
    SiteFooter,
    BenefitList,
  },
  computed: {
    isUserAuthenticated() {
      const sessionStore = useSessionStore();
      return sessionStore.userId !== null;
    },
  },
  methods: {
    handleScroll() {
      const heroSection = this.$el.querySelector(".organism-hero-section");

      if (!heroSection) return; // Exit if hero section not found

      const rect = heroSection.getBoundingClientRect();
      const currentScrollY = window.scrollY;

      if (currentScrollY === 0) {
        this.logoRotation = 0; // Reset logo rotation to 0
      } else if (currentScrollY > this.lastScrollY) {
        // Scrolling down
        this.logoRotation += 5 * 1.5; // Rotate clockwise
      } else {
        // Scrolling up
        this.logoRotation -= 5 * 1.5; // Rotate anticlockwise
      }
      this.lastScrollY = currentScrollY; // Update last scroll position

      // Toggle dark mode based on hero section visibility
      if (rect.bottom <= 81) {
        this.enableDarkMode = false; // Disable dark mode when hero is scrolled past
      } else {
        this.enableDarkMode = true; // Enable dark mode when hero is visible
      }
    },
    getTruncatedDescription(description, maxLength) {
      return truncateText(description, maxLength); // Adjust the character limit as needed
    },
    handleLoginSelection() {
      this.openLoginModal();
    },
    handleSignUpSelection() {
      this.openSignUpModal();
    },
    handleBookDemo() {
      // Navigate to contact or open demo booking modal
      // For now, open signup modal as placeholder
      this.openSignUpModal();
    },
    handleStartTrial() {
      // Open signup modal to start free trial
      this.openSignUpModal();
    },
    scrollToPricing() {
      const pricingSection = this.$el.querySelector(
        ".organism-pricing-preview"
      );
      if (pricingSection) {
        pricingSection.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    },
    handleFlightLogsSelection() {
      this.$router.push("/flight-logs");
    },
    openLoginModal() {
      this.isLoginModalOpen = true;
    },
    closeLoginModal() {
      this.isLoginModalOpen = false;
      this.errorMessage = "";
    },
    openSignUpModal() {
      this.isSignUpModalOpen = true;
    },
    closeSignUpModal() {
      this.isSignUpModalOpen = false;
      this.errorMessage = "";
    },
    navigateToListing(listingId) {
      // this.$router.push(`/listing/${listingId}`);
      this.$router.push({
        path: `/listing`,
        query: { listingId: listingId },
      });
    },
    async handleLoginButton(data) {
      // Track login attempt
      amplitude.track("Login Form Submitted", {
        type: "login",
        source: "home_page",
        email_domain: data.email.split("@")[1] || "unknown",
        timestamp: new Date().toISOString(),
      });

      this.errorMessage = "";

      try {
        const user = await loginService.loginUser(data.email, data.password);
        const sessionStore = useSessionStore();
        sessionStore.setUserId(user.uid);

        // Track successful login
        amplitude.track("Login Success", {
          userId: user.uid,
          email: user.email,
          source: "home_page",
          timestamp: new Date().toISOString(),
        });

        this.closeLoginModal();
        this.$router.push("/pilot-dashboard");
      } catch (error) {
        this.errorMessage = error.message;

        // Track login failure with specific error type
        let errorType = "unknown";
        if (error.message.includes("password")) {
          errorType = "wrong_password";
        } else if (error.message.includes("No account")) {
          errorType = "user_not_found";
        } else if (error.message.includes("network")) {
          errorType = "network_error";
        } else if (error.message.includes("Too many failed")) {
          errorType = "too_many_attempts";
        }

        amplitude.track("Login Failed", {
          error_type: errorType,
          error_message: error.message,
          source: "home_page",
          timestamp: new Date().toISOString(),
        });
      }
    },
    async handleSignUp(data) {
      console.log("Sign up form submitted!");
      this.errorMessage = "";

      amplitude.track("SignUp Form Submitted", {
        type: "signUp",
        source: "home_page",
        timestamp: new Date().toISOString(),
      });

      try {
        const user = await userAuth.signUpUser(
          data.username,
          data.email,
          data.password,
          data.confirmPassword
        );
        console.log("Signed in as:", user.email);
        const sessionStore = useSessionStore();
        sessionStore.setUserId(user.uid);

        // Track successful signup
        amplitude.track("SignUp Success", {
          userId: user.uid,
          email: user.email,
          displayName: user.displayName || data.username,
          source: "home_page",
          timestamp: new Date().toISOString(),
        });

        this.closeSignUpModal();
        this.$router.push("/pilot-dashboard");
      } catch (error) {
        console.log(error);
        this.errorMessage = error.message;

        // Track signup failure with specific error type
        let errorType = "unknown_error";
        if (error.message.includes("already in use")) {
          errorType = "email_already_in_use";
        } else if (error.message.includes("Invalid email")) {
          errorType = "invalid_email";
        } else if (error.message.includes("weak")) {
          errorType = "weak_password";
        } else if (error.message.includes("network")) {
          errorType = "network_error";
        } else if (error.message.includes("match")) {
          errorType = "passwords_dont_match";
        } else if (error.message.includes("fill in all")) {
          errorType = "missing_fields";
        } else if (error.message.includes("disabled")) {
          errorType = "account_disabled";
        }

        // Track signup failure with error information
        amplitude.track("SignUp Failed", {
          errorCode: error.code || "unknown_error",
          errorType: errorType,
          errorMessage: error.message,
          source: "home_page",
          timestamp: new Date().toISOString(),
        });
      }
    },
    logout() {
      signOut(auth)
        .then(() => {
          const sessionStore = useSessionStore();
          sessionStore.clearUser();
          this.$router.push("/");
        })
        .catch(() => {
          this.$router.push("/");
        });
    },
  },
  watch: {
    isUserAuthenticated: {
      handler(isAuthenticated) {
        if (isAuthenticated) {
          this.$router.push("/pilot-dashboard");
        }
      },
      immediate: true,
    },
  },
  mounted() {
    // Redirect authenticated users to pilot dashboard
    if (this.isUserAuthenticated) {
      this.$router.push("/pilot-dashboard");
      return;
    }

    window.addEventListener("scroll", this.handleScroll);
    this.isMobile = isMobile();
    // Dynamically load the Bokun script
    // const script = document.createElement("script");
    // script.src =
    //   "https://widgets.bokun.io/assets/javascripts/apps/build/BokunWidgetsLoader.js?bookingChannelUUID=55418838-f54d-4a31-99eb-f1c2c721125e";
    // script.async = true;
    // document.body.appendChild(script);
  },
  beforeUnmount() {
    window.removeEventListener("scroll", this.handleScroll);
  },
};
</script>

<style scoped>
.app-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: #fff;
}

#bokun_b51d084c_48fc_453c_b606_187a6002c30f {
  display: inline-block;
  padding: 10px 20px;
  background: #408c3d;
  border-radius: 5px;
  box-shadow: none;
  font-weight: 600;
  font-size: 16px;
  text-decoration: none;
  text-align: center;
  color: #ffffff;
  border: none;
  cursor: pointer;
  transition: background 0.2s ease;
}
#bokun_b51d084c_48fc_453c_b606_187a6002c30f:hover {
  background: #285726;
}
#bokun_b51d084c_48fc_453c_b606_187a6002c30f:active {
  background: #30682e;
}

.line-divider {
  border: none;
  border-top: 1px solid #e6e4e4;
}

/* Main Content */
.mobile-main-content {
  margin-top: 20px;
  padding: 20px;
}

.full-screen-section {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100vh;
  padding: 20px;
  text-align: center;
  background-color: #000;
}

.hero-title {
  margin-top: 15rem;
  font-size: 4rem;
  font-weight: 100;
}

.hero-subheading {
  margin-top: 1rem;
  font-size: 1.5rem;
  font-weight: 100;
  color: #bebebe;
  /* color: #000; */
}

.listing-section,
.experiences-section,
.gift-section,
.host-section {
  background-color: #f8f9fa;
}

.number-section {
  display: flex;
  flex-direction: column;
  align-items: center; /* Center align items horizontally */
  text-align: center; /* Center align text */
  padding-top: 5rem;
  padding-bottom: 2rem;
}

.number-cards {
  display: flex;
  justify-content: space-around;
  flex-wrap: wrap;
}

.number-card {
  border-radius: 8px;
  margin: 10px;
  padding: 10px;
  width: 250px;
  align-items: center;
  text-align: center;
}

.number-card h1 {
  font-size: 4rem;
  color: #222;
  text-align: center;
  margin-bottom: 0.5rem;
}

.number-card p {
  font-size: 1.2rem;
  color: #7e7e7e;
  text-align: center;
}

.subscribe-section {
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: center;
  text-align: left;
  padding: 15rem 2rem;
}

.problem-section {
  display: flex;
  flex-direction: row;
  align-items: center;
  text-align: center;
  padding: 10rem 5rem;
}

.problem-cards {
  display: flex;
  width: 100%;
  justify-content: space-around;
  flex-wrap: wrap;
}

.problem-image {
  flex: 2;
  width: 100%;
}

.problem-card {
  flex: 1;
  width: 100%;
  padding: 0 20px;
}

.problem-card h2 {
  font-size: 2rem;
  color: #222;
  text-align: left;
}

.problem-card h3 {
  font-size: 1.5rem;
  color: #222;
  text-align: left;
}

.problem-card p {
  font-size: 1rem;
  color: #7e7e7e;
  text-align: left;
}

.problem-card span {
  color: #000;
}

.problem-card span:hover {
  text-decoration: underline;
}

.learn-more {
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 5px;
  display: flex;
  cursor: pointer;
}

.points-section {
  display: flex;
  flex-direction: column;
  align-items: center; /* Center align items horizontally */
  text-align: center; /* Center align text */
  padding-bottom: 6rem;
}

.vision-section {
  display: flex;
  flex-direction: column;
  align-items: center; /* Center align items horizontally */
  text-align: center; /* Center align text */
  padding: 10rem 5rem;
}

.faq-home-section {
  display: flex;
  justify-content: center;
  padding: 10rem 5rem;
  background-color: #fff;
}

.vision-title {
  font-size: 3rem;
  margin-bottom: 20px;
  text-align: left;
}

.vision-description {
  font-size: 1.2rem;
  padding: 1rem 2rem; /* Add horizontal padding for better spacing */
  color: #7e7e7e; /* Change text color to white for contrast */
  width: 75%; /* Width of the description box */
}

@media (max-width: 768px) {
  .vision-section {
    padding: 5rem 1rem;
  }

  .faq-home-section {
    padding: 5rem 1rem;
  }

  .vision-description {
    width: 100%;
    padding: 0;
  }
}

.vision-cards {
  display: flex;
  justify-content: space-around;
  flex-wrap: wrap;
}

.vision-card {
  /* background: #f8f7f7; */
  border-radius: 8px;
  margin: 10px;
  padding: 10px;
  width: 350px;
  align-items: left;
  text-align: left;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}

.vision-card h2 {
  font-size: 2rem;
  color: #222;
  text-align: left;
  margin: 0.5rem 0;
}

.vision-card h3 {
  font-size: 1.5rem;
  color: #222;
  text-align: left;
  margin: 0.5rem 0;
  /* font-weight: 500; */
}

.vision-card p {
  font-size: 1rem;
  color: #7e7e7e;
  text-align: left;
}

.vision-card span {
  color: #000;
}

.vision-card span:hover {
  text-decoration: underline;
}

.learn-more {
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 5px;
  display: flex;
  cursor: pointer;
}

.card-image {
  width: 100%;
  height: 380px;
  object-fit: cover;
  border-radius: 8px; /* Rounded top corners */
}

.card-image1 {
  width: 100%;
  height: 380px;
  object-fit: contain;
  border-radius: 8px; /* Rounded top corners */
}

.card-btn {
  background-color: #3b82f6; /* Button color */
  color: white;
  padding: 10px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  margin-top: 10px;
}

.card-btn:hover {
  background-color: #679ef6; /* Lighter button color on hover */
}

.avatar {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  object-fit: cover;
}

.grid-container {
  display: grid;
  grid-template-columns: repeat(2, 1fr); /* Two columns */
  gap: 40px; /* Space between grid items */
  padding: 3rem 5rem;
  margin-bottom: 5rem;
  width: 100%;
}

.grid-item {
  border-radius: 8px; /* Rounded corners */
  overflow: hidden; /* Prevent overflow */
  cursor: pointer;
}

.flight-content {
  display: flex; /* Use flexbox for layout */
  max-height: 280px;
  flex-direction: row;
}

.image-container {
  position: relative; /* Position relative for absolute positioning of the chip */
}

.flight-image {
  width: 310px;
  height: 280px;
  border-radius: 12px;
  object-fit: cover;
}

.chip {
  position: absolute; /* Position the chip absolutely */
  top: 10px; /* Distance from the top */
  right: 10px; /* Distance from the right */
  background-color: #fff; /* Background color of the chip */
  color: #000; /* Text color */
  padding: 5px 10px; /* Padding for the chip */
  border-radius: 16px; /* Rounded corners for the chip */
  /* box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2); Optional: shadow for depth */
  font-size: 0.8rem; /* Font size */
  font-weight: 500; /* Bold text */
}

.flight-details {
  flex: 1;
  padding: 0 20px; /* Padding for text content */
  display: flex;
  justify-content: space-between;
  flex-direction: column; /* Stack text vertically */
}

.flight-details h4 {
  font-size: 0.8rem; /* Increase font size for location */
  color: #666;
  margin-bottom: 5px;
}

.flight-details h2 {
  font-size: 1.5rem; /* Increase font size for title */
  color: #333;
}

.flight-details p {
  font-size: 0.8rem;
  color: #666;
  /* margin-bottom: 10px; */
}

.flight-header {
  display: flex;
  flex: 1;
  flex-direction: column;
  margin-bottom: 1rem;
}

.flight-info {
  display: flex;
  flex: 2.5;
  flex-direction: column;
  justify-content: space-between;
}

.flight-footer {
  display: flex;
  flex: 0.5;
  flex-direction: row;
  align-items: flex-end;
  justify-content: space-between;
}

.flight-footer p {
  font-size: 1rem;
  color: #000 !important;
}

.flight-footer span {
  font-size: 0.8rem;
  color: #949494;
}

.price-container {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 5px;
}

.main-header {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-top: 4rem;
  padding: 0rem 5rem;
}

.main-header h2 {
  color: #000;
}

.main-title {
  font-size: 3rem;
  color: #000;
  margin-bottom: 2rem;
  font-weight: bold;
}

@media (max-width: 768px) {
  .main-title {
    font-size: 2rem;
    margin-bottom: 2.5rem;
    font-weight: bold;
  }
}

.main-description {
  font-size: 1.2rem;
  color: #7e7e7e;
}

.main-btn {
  background-color: #fff;
  display: flex;
  align-items: center;
  width: 80px;
  height: 50px;
  border: 1px solid #e5e7eb !important;
  border-radius: 25px;
  cursor: pointer;
  padding: 10px;
  outline: none;
  transition: background-color 0.3s ease;
}

.banner-image {
  width: 100%;
  border-radius: 8px;
  object-fit: contain;
}

.main-banner-image {
  width: 75%;
  height: 75%;
  object-fit: contain;
}

.logout-button {
  background-color: #e5e7eb !important;
  border: none;
  border-radius: 12px;
  color: white;
  width: 45px;
  height: 45px;
  display: flex;
  align-items: center;
  justify-content: center;
  outline: none;
}

.login-button {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 80px;
  height: 50px;
  border: 1px solid #e0e0e0 !important;
  border-radius: 25px;
  cursor: pointer;
  padding: 10px;
  outline: none;
  transition: background-color 0.3s ease;
}

.login-button:hover {
  background-color: #e0e0e0;
}

.login-button-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.user-icon {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: #f1f1f1;
  border: 1px solid #d3d3d3;
  margin-right: 5px;
  position: relative;
}

.logout-button:hover {
  background-color: rgb(235, 235, 235) !important;
}

.dropdown-menu {
  position: absolute;
  top: 75px;
  right: 45px;
  background-color: white;
  border-radius: 10px;
  box-shadow: 0px 8px 16px rgba(0, 0, 0, 0.3);
  width: 250px;
  z-index: 100;
  padding: 10px;
}

.dropdown-menu ul {
  list-style-type: none;
  padding: 0;
  margin: 0;
}

.dropdown-menu ul li {
  color: #000;
  font-size: 14px;
  padding: 10px;
  cursor: pointer;
}

.dropdown-menu ul li:hover {
  background-color: #f0f0f0;
}

.dropdown-menu hr {
  margin: 10px 0;
  border: none;
  border-top: 1px solid #eee;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000; /* Ensure it is above all other content */
}

.modal-content {
  background: white;
  padding: 20px;
  border-radius: 12px;
  width: 350px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  text-align: center;
}

.modal-title {
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  color: #333;
}

.modal-form {
  display: flex;
  flex-direction: column;
}

.form-group {
  flex: 1;
  width: 100%;
  text-align: left;
  flex-direction: column;
}

.form-group label {
  display: block;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
  color: #555;
}

.form-group input {
  width: 100%;
  padding: 10px;
  font-size: 1rem;
  border: 1px solid #ccc;
  border-radius: 8px;
  outline: none;
}

.form-group input:focus {
  border-color: #000; /* Focus color */
}

.modal-submit {
  background-color: #000;
  color: white;
  padding: 10px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  font-size: 1rem;
  margin-top: 1rem;
}

.modal-submit:hover {
  background-color: #1d1d1d;
}

.benefits-container {
  display: flex;
  justify-content: center;
  width: 100%;
  max-width: 800px;
  margin-top: 2rem;
}

@media (max-width: 768px) {
  .benefits-container {
    padding: 0 0rem;
  }
}
</style>
